import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Star, ArrowRight, ChevronRight, Shield, Clock, Award, CheckCircle, Search, Truck, FileText, Wrench, Key } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const iconMap = { Search, Truck, FileText, Wrench, Shield, Key };
const whyIcons = [Shield, Clock, Award, CheckCircle];

const BG_EUROPE = "https://images.pexels.com/photos/11190484/pexels-photo-11190484.jpeg?auto=compress&cs=tinysrgb&w=1920";
const BG_MOSCOW = "https://images.pexels.com/photos/8817706/pexels-photo-8817706.jpeg?auto=compress&cs=tinysrgb&w=1920";

function ParallaxBackground() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const zone = window.innerHeight * 2.5;
      setProgress(Math.min(Math.max(y / zone, 0), 1));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const europeOpacity = 1 - progress;
  const moscowOpacity = progress;
  const shift = progress * -30;

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${BG_EUROPE})`, backgroundSize: "cover", backgroundPosition: "center",
        opacity: europeOpacity,
        transform: `translateY(${shift * 0.5}px) scale(1.05)`,
        willChange: "opacity, transform",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${BG_MOSCOW})`, backgroundSize: "cover", backgroundPosition: "center",
        opacity: moscowOpacity,
        transform: `translateY(${shift}px) scale(1.05)`,
        willChange: "opacity, transform",
      }} />
      <div className="absolute inset-0 bg-black/40" />
    </div>
  );
}

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const settings = useSettings();
  const navigate = useNavigate();
  const s = settings || {};

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const why = [0, 1, 2, 3].map(i => ({
    icon: whyIcons[i],
    title: s[`why_us_${i + 1}_title`] || ["Юридическая чистота", "Быстрые сроки", "Гарантия качества", "Под ключ"][i],
    desc: s[`why_us_${i + 1}_desc`] || ["Проверяем каждый автомобиль по всем базам Европы и России.", "От заказа до получения — 2-4 недели.", "Независимая экспертиза состояния перед покупкой.", "Логистика, таможня, регистрация — мы берём всё на себя."][i],
  }));

  const stats = [
    { val: s.stats_clients || "500+", label: s.stats_clients_label || "Клиентов" },
    { val: s.stats_cars || "1000+", label: s.stats_cars_label || "Автомобилей" },
    { val: s.stats_years || "15+", label: s.stats_years_label || "Лет опыта" },
    { val: s.stats_reviews || "98%", label: s.stats_reviews_label || "Довольных клиентов" },
  ];

  const glass = "bg-white/[0.04] border border-white/[0.08] backdrop-blur-xl";

  return (
    <div data-testid="home-page" className="relative">
      <ParallaxBackground />
      <div className="relative" style={{ zIndex: 1 }}>

        {/* HERO */}
        <section className="min-h-[92vh] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-28 sm:py-32 md:py-0 w-full">
            <div className="max-w-3xl">
              <p className="text-xs sm:text-sm font-medium tracking-[0.25em] uppercase text-[#D4AF37] mb-4 sm:mb-6 animate-fade-in-up stagger-1">{s.logo_text || "AutoPride Group"}</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] animate-fade-in-up stagger-2"
                style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>
                {(s.hero_title || "Автомобили из Европы под ключ").split(" ").slice(0, -2).join(" ")}{" "}
                <span className="gold-text">{(s.hero_title || "Автомобили из Европы под ключ").split(" ").slice(-2).join(" ")}</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-white/70 font-light mt-4 sm:mt-6 max-w-xl leading-relaxed animate-fade-in-up stagger-3"
                style={{ textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
                {s.hero_subtitle || "Профессиональный подбор, пригон и таможенное оформление автомобилей премиум-класса из Европы."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 sm:mt-10 animate-fade-in-up stagger-4">
                <Button data-testid="hero-cta-btn" onClick={() => navigate("/podbor")}
                  className="rounded-md border-2 border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-[#F4D03F] font-semibold px-6 sm:px-8 py-5 sm:py-6 uppercase tracking-widest text-xs sm:text-sm transition-all duration-300 shadow-lg shadow-[#D4AF37]/20">
                  {s.hero_cta_text || "Подобрать авто"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button data-testid="hero-portfolio-btn" onClick={() => navigate("/catalog")} variant="ghost"
                  className="rounded-md border border-white/30 text-white/90 hover:text-white hover:bg-white/10 hover:border-white/50 font-medium px-6 sm:px-8 py-5 sm:py-6 uppercase tracking-widest text-xs sm:text-sm transition-all duration-300">
                  Каталог <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className={`py-10 sm:py-14 border-y border-white/[0.08] ${glass}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-12">
              {stats.map((st, i) => (
                <div key={i} data-testid={`stat-${i}`} className="text-center">
                  <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold gold-text" style={{ fontFamily: "'Playfair Display', serif" }}>{st.val}</p>
                  <p className="text-[10px] sm:text-xs text-white/60 mt-1 sm:mt-2 uppercase tracking-widest">{st.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY US */}
        <section className="py-14 sm:py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="mb-10 sm:mb-14">
              <p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-3">Преимущества</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                {(s.why_us_title || "Почему выбирают нас").split(" ").slice(0, -1).join(" ")}{" "}
                <span className="gold-text">{(s.why_us_title || "Почему выбирают нас").split(" ").slice(-1)}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {why.map((w, i) => (
                <div key={i} data-testid={`why-card-${i}`} className={`${glass} p-5 sm:p-7 hover:border-[#D4AF37]/30 transition-all duration-500 group rounded-md`}>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 border border-[#D4AF37]/30 flex items-center justify-center mb-4 sm:mb-5 group-hover:border-[#D4AF37] transition-colors rounded-md">
                    <w.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] stroke-[1.5]" />
                  </div>
                  <h3 className="text-base sm:text-lg font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{w.title}</h3>
                  <p className="text-white/50 font-light text-xs sm:text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        {services.length > 0 && (
          <section className="py-14 sm:py-20 md:py-24 border-t border-white/[0.06]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-14 gap-3">
                <div>
                  <p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-3">Услуги</p>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                    {(s.services_title || "Полный спектр услуг").split(" ").slice(0, -1).join(" ")}{" "}
                    <span className="gold-text">{(s.services_title || "Полный спектр услуг").split(" ").slice(-1)}</span>
                  </h2>
                </div>
                <Button data-testid="services-link-btn" onClick={() => navigate("/services")} variant="ghost"
                  className="rounded-md text-[#D4AF37] hover:bg-[#D4AF37]/10 uppercase tracking-widest text-xs self-start sm:self-auto">
                  Все услуги <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {services.map((svc, i) => {
                  const IconComp = iconMap[svc.icon] || Search;
                  return (
                    <div key={svc.id || i} data-testid={`home-service-${i}`} className={`${glass} p-5 sm:p-7 hover:border-[#D4AF37]/30 transition-all duration-500 group rounded-md`}>
                      <div className="w-10 h-10 sm:w-12 sm:h-12 border border-[#D4AF37]/30 flex items-center justify-center mb-4 sm:mb-5 group-hover:border-[#D4AF37] transition-colors rounded-md">
                        <IconComp className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] stroke-[1.5]" />
                      </div>
                      <h3 className="text-base sm:text-lg font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{svc.title}</h3>
                      <p className="text-white/50 font-light text-xs sm:text-sm leading-relaxed">{svc.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* REVIEWS */}
        {reviews.length > 0 && (
          <section className="py-14 sm:py-20 md:py-24 border-t border-white/[0.06]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-14 gap-3">
                <div>
                  <p className="text-xs sm:text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-3">Отзывы</p>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                    {(s.reviews_title || "Отзывы клиентов").split(" ").slice(0, -1).join(" ")}{" "}
                    <span className="gold-text">{(s.reviews_title || "Отзывы клиентов").split(" ").slice(-1)}</span>
                  </h2>
                </div>
                <Button data-testid="reviews-link-btn" onClick={() => navigate("/reviews")} variant="ghost"
                  className="rounded-md text-[#D4AF37] hover:bg-[#D4AF37]/10 uppercase tracking-widest text-xs self-start sm:self-auto">
                  Все отзывы <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {reviews.map((r, i) => (
                  <div key={r.id || i} data-testid={`home-review-${i}`} className={`${glass} p-5 sm:p-7 hover:border-[#D4AF37]/30 transition-all duration-500 rounded-md`}>
                    <div className="flex gap-0.5 mb-3 sm:mb-4">
                      {Array.from({ length: r.rating || 5 }).map((_, j) => (
                        <Star key={j} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#D4AF37] fill-[#D4AF37]" />
                      ))}
                    </div>
                    <p className="text-white/60 font-light text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 line-clamp-4">"{r.text}"</p>
                    <div className="border-t border-white/[0.06] pt-3 sm:pt-4">
                      <p className="font-medium text-xs sm:text-sm">{r.author}</p>
                      {r.car && <p className="text-[10px] sm:text-xs text-[#D4AF37] mt-0.5">{r.car}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 sm:py-24 md:py-28 border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-4 sm:mb-5" style={{ fontFamily: "'Playfair Display', serif", textShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
              {(s.cta_title || "Готовы заказать автомобиль?").split(" ").slice(0, -1).join(" ")}{" "}
              <span className="gold-text">{(s.cta_title || "Готовы заказать автомобиль?").split(" ").slice(-1)}</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-white/60 font-light max-w-2xl mx-auto mb-8 sm:mb-10">
              {s.cta_subtitle || "Оставьте заявку через подбор или свяжитесь с нашими менеджерами напрямую."}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button data-testid="cta-configurator-btn" onClick={() => navigate("/podbor")}
                className="rounded-md border-2 border-[#D4AF37] bg-[#D4AF37] text-black hover:bg-[#F4D03F] font-semibold px-6 sm:px-8 py-5 sm:py-6 uppercase tracking-widest text-xs sm:text-sm transition-all duration-300 shadow-lg shadow-[#D4AF37]/20">
                Подобрать авто <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="cta-contacts-btn" onClick={() => navigate("/contacts")} variant="ghost"
                className="rounded-md border border-white/30 text-white/90 hover:text-white hover:bg-white/10 hover:border-white/50 font-medium px-6 sm:px-8 py-5 sm:py-6 uppercase tracking-widest text-xs sm:text-sm transition-all duration-300">
                Наши контакты <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
